import { useEnvironments } from "@app-context/environments/context";
import { restServerBase } from "@app-utils/restServer";
import {
  checkUserEnabled,
  checkUserPermission,
  checkUserPo,
  checkUserPoId,
  checkUserType,
  INTERNAL_PO_IDS,
} from "@enfusion-ui/core";
import { useMounted, useRefCallback } from "@enfusion-ui/hooks";
import { User, UserFlagKeys, UserPermKeys } from "@enfusion-ui/types";
import { mobilePostMessage } from "@enfusion-ui/utils";
import { AuthContext, errorToast, REST_API } from "@enfusion-ui/web-core";
import Keycloak from "keycloak-js";
import * as React from "react";

const AUTH_TOKEN_KEY = "ai-tool-auth-tk";

export const AuthProvider: React.FC<{
  user: User | null;
}> = ({ children, user: baseUser }) => {
  const [user, setUser] = React.useState<User | null>(baseUser);
  const [firstHeartbeatDone, setFirstHeartbeatDone] = React.useState(false);
  const keycloakRef = React.useRef<Keycloak.KeycloakInstance>(Keycloak());
  const isMounted = useMounted();
  const { authEnvironment } = useEnvironments();

  React.useEffect(() => {
    let timeoutId = -1;

    if (user) {
      if (firstHeartbeatDone) {
        const args = {
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            Authorization: `Basic ${btoa(
              `${user.token.preferred_username}:${user.token.enfusion_token}`
            )}`,
            SSO_SESSION_ID:
              user.token.enfusion_origin_session || user.token.session_state,
          },
        };
        heartbeat(args).finally(() => {
          timeoutId = setInterval(
            () => heartbeat(args),
            30000
          ) as unknown as number;
        });
      }
    }

    return () => {
      clearInterval(timeoutId);
    };
  }, [user, firstHeartbeatDone]);

  React.useEffect(() => {
    if (authEnvironment) {
      try {
        keycloakRef.current = Keycloak({
          url:
            authEnvironment.IDPUrl ??
            "https://login-uat-us01.enfusionsystems.com/auth",
          realm: "enfusion",
          clientId: "Enfusion",
        });

        keycloakRef.current
          .init({
            onLoad: "check-sso",
            checkLoginIframe: false,
            enableLogging: true,
            flow: "hybrid",
            silentCheckSsoFallback: false,
            redirectUri: window.location.origin,
            silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso`,
          })
          .then((authenticate) => {
            if (authenticate) onSuccess(keycloakRef.current);
          })
          .catch((reason) => {
            errorToast("Keycloak error. Click to reload", reason, {
              onClick: () => window.location.reload(),
            });
            console.warn(reason);
          });
      } catch (error) {
        console.warn("keycloak issue", error);
      }
    }
  }, [authEnvironment]);

  const heartbeat = useRefCallback(
    async (userConfig?: Record<string, unknown>) => {
      try {
        const res = await restServerBase<boolean>(
          "/internal/api/utility/heartbeat",
          authEnvironment.restAPIUrl,
          userConfig
        );
        if (res[0] === true) {
          mobilePostMessage({
            source: "mobile-app-auth",
            action: "duplicate-login",
          });
        }

        return res[1];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        console.warn("Heartbeat error", err);
        // setApiError(err.message);
      }
    },
    [authEnvironment]
  );

  const onSuccess = useRefCallback(
    async (keycloakRef) => {
      const token = keycloakRef.tokenParsed;
      if (token) {
        const storedAuthToken = localStorage.getItem(AUTH_TOKEN_KEY);
        const res = await heartbeat({
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            Authorization: `Basic ${btoa(
              `${token.preferred_username}:${token.enfusion_token}`
            )}`,
            SSO_SESSION_ID:
              token.enfusion_origin_session || token.session_state,
          },
        });
        setFirstHeartbeatDone(true);
        const authToken = res?.headers.get("x-auth-token") ?? storedAuthToken;
        if (authToken) localStorage.setItem(AUTH_TOKEN_KEY, authToken);
        const { WSSERVERID } = Object.fromEntries(
          document.cookie
            .split("; ")
            .map((x) => x.split(/=(.*)$/, 2).map(decodeURIComponent))
        );
        const user = {
          username: token.preferred_username,
          adminUser: token.enfusion_admin,
          token,
          authToken,
          WSSERVERID,
          flags: {
            FactSet: {
              enabled: !!token.factset_idp_id,
              idpId: token.factset_idp_id,
            },
          },
        } as User;
        if (isMounted()) {
          setUser(user);
        }
      }
    },
    [user]
  );

  const login = useRefCallback(
    (_user: User, _secureAPIToken: string) => {
      keycloakRef.current.login();
    },
    [keycloakRef.current]
  );

  const logout = useRefCallback(async () => {
    try {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      console.info("/internal/api/utility/logout called");
      await REST_API.UTILITY.LOGOUT();
    } catch (err) {
      console.warn("error hitting /logout", err);
    }
    if (isMounted()) {
      setUser(null);
    }
    if (keycloakRef.current) {
      try {
        console.info("keycloak.logout called");
        mobilePostMessage({
          source: "mobile-app-auth",
          action: "logged-out",
          message: "logout called",
        });
        await keycloakRef.current.logout();
      } catch (err) {
        console.error("error logging out keycloak", err);
      }
    } else {
      mobilePostMessage({
        source: "mobile-app-auth",
        action: "logged-out",
        message: "clear user data",
      });
    }
  }, [keycloakRef.current]);

  const continueSessionDup = () => Promise.resolve();

  const terminateCurrentSession = () => Promise.resolve();

  const terminateOtherSession = () => Promise.resolve();

  const isEnabled = useRefCallback(
    (flag: UserFlagKeys) => {
      if (!user) return false;
      return checkUserEnabled(user, flag);
    },
    [user]
  );

  const hasPerm = useRefCallback(
    (perm: UserPermKeys) => {
      if (!user) return false;
      return checkUserPermission(user, perm);
    },
    [user]
  );

  const hasRestrictedPerm = useRefCallback(
    (perm: UserPermKeys) => {
      if (isAdmin()) return false;
      return hasPerm(perm);
    },
    [user]
  );

  const isUserType = useRefCallback(
    (userType: string) => {
      if (!user) return false;
      return checkUserType(user, userType);
    },
    [user]
  );

  const isPo = useRefCallback(
    (poName: string, caseSensitive = true) => {
      if (!user) return false;
      return checkUserPo(user, poName, caseSensitive);
    },
    [user]
  );

  const isPoId = useRefCallback(
    (poId: number) => {
      if (!user) return false;
      return checkUserPoId(user, poId);
    },
    [user]
  );

  const isAdmin = useRefCallback(() => {
    if (!user || !user.adminUser) return false;
    return true;
  }, [user]);

  const isInternalPo = useRefCallback(() => {
    if (!user) return false;
    return isPoId(INTERNAL_PO_IDS.Enfusion) || isPoId(INTERNAL_PO_IDS.Demo);
  }, [user]);

  const isEnfusionAdmin = useRefCallback(() => {
    return isAdmin() && isPoId(INTERNAL_PO_IDS.Enfusion);
  }, [isAdmin, isPo]);

  return (
    <AuthContext.Provider
      value={{
        apiError: null,
        user,
        secureAPIToken: "",
        isLoggedInDesktop: false,
        login,
        logout,
        continueSessionDup,
        terminateOtherSession,
        terminateCurrentSession,
        isUserType,
        isEnabled,
        hasPerm,
        isAdmin,
        isEnfusionAdmin,
        isPo,
        isInternalPo,
        isPoId,
        hasRestrictedPerm,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
